import { useCallback } from "react";
import { useAnalytics } from "@/contexts/AnalyticsContext";
import { useModel } from "@/contexts/ModelContext";
import { AnalyticsFlowEvent } from "@/analytics/types";
import { downloadAnnotatedImageFromDOM } from "@utils/canvasUtils";
import { createAnnotatedVideo } from "@utils/videoEditing";
import { useFps } from "@/contexts/FpsContext";
import { DEFAULT_CONFIDENCE_THRESHOLD } from "@/utils/predictions";
import { useCurrentMedia } from "@/store";

/**
 * Modern file download utility with File System Access API fallback
 * Downloads a file using the native file picker when available, or falls back to traditional download method
 * @param file - The File object to download
 */
async function downloadFile(file: File): Promise<void> {
    // Check if File System Access API is supported (Chrome 86+, Edge 86+)
    if ("showSaveFilePicker" in window) {
        try {
            // Use the modern File System Access API
            const handle = await (window as any).showSaveFilePicker({
                suggestedName: file.name,
                types: [
                    {
                        description: file.type.startsWith("video/") ? "Video files" : "Image files",
                        accept: { [file.type]: [`.${file.name.split(".").pop()}`] }
                    }
                ]
            });

            const writable = await handle.createWritable();
            await writable.write(file);
            await writable.close();
            return;
        } catch (error) {
            // User cancelled or API failed, fallback to traditional method
            if (error instanceof Error && error.name === "AbortError") {
                return; // User cancelled, don't fallback
            }
        }
    }

    // Fallback to traditional download method
    const url = URL.createObjectURL(file);
    const a = document.createElement("a");
    a.href = url;
    a.download = file.name;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

interface UseDownloadAnnotatedFileOptions {
    /**
     * Optional watermark URL to include in the downloaded file
     * Defaults to "https://rapid.roboflow.com"
     */
    watermarkUrl?: string;
    /**
     * Optional task identifier for analytics tracking
     */
    task?: string;
}

/**
 * Shared hook for downloading annotated files (images or videos)
 * Handles both image and video downloads with watermarks, progress tracking, and analytics
 *
 * @param options - Configuration options for the download
 * @returns A callback function to trigger the download
 *
 * @example
 * ```tsx
 * const handleDownload = useDownloadAnnotatedFile({ task: 'object-detection' });
 *
 * <button onClick={handleDownload}>
 *   Download Annotated File
 * </button>
 * ```
 */
export function useDownloadAnnotatedFile(options: UseDownloadAnnotatedFileOptions = {}) {
    const { watermarkUrl = "https://rapid.roboflow.com", task } = options;

    const { predictionsForFile, isDownloading, setIsDownloading, setDownloadProgress } = useModel();
    const { track } = useAnalytics();
    const currentMedia = useCurrentMedia();
    const { fps } = useFps();
    const handleDownload = useCallback(async () => {
        if (!currentMedia?.file.id || isDownloading) {
            return;
        }

        setIsDownloading(true);

        track({
            event: AnalyticsFlowEvent.DOWNLOAD_ANNOTATED_CLICKED,
            data: { type: currentMedia?.file.fileType, task, file_uuid: currentMedia?.file.id }
        });

        try {
            if (!currentMedia?.file.isVideo) {
                // Download annotated image from DOM
                const success = await downloadAnnotatedImageFromDOM({
                    includeWatermark: true,
                    watermarkQrUrl: watermarkUrl
                });
                if (!success) {
                    alert("Could not find image or annotation canvas to download annotated image.");
                }
            } else {
                // Video: use WebAssembly FFMPEG
                if (!currentMedia?.file) {
                    alert("No video file available for download.");
                    return;
                }

                console.log("🎬 [DOWNLOAD] Starting WebAssembly video annotation...");

                // Set up progress callback for user feedback
                const progressCallback = (progress: number) => {
                    console.log(`🎬 [DOWNLOAD] Progress: ${progress}%`);
                    setDownloadProgress(progress);
                };

                try {
                    const annotatedVideoFile = await createAnnotatedVideo(
                        currentMedia?.file.file,
                        predictionsForFile,
                        DEFAULT_CONFIDENCE_THRESHOLD,
                        fps,
                        0.4, // font scale
                        2, // box thickness
                        progressCallback,
                        true, // includeWatermark
                        watermarkUrl
                    );

                    // Download the annotated video using modern File System Access API with fallback
                    await downloadFile(annotatedVideoFile);

                    console.log("🎬 [DOWNLOAD] ✅ Video download completed successfully");
                } catch (error) {
                    console.error("🎬 [DOWNLOAD] ❌ Error creating annotated video:", error);
                    alert(
                        `Error creating annotated video: ${error instanceof Error ? error.message : "Unknown error"}`
                    );
                    return;
                }
            }

            track({
                event: AnalyticsFlowEvent.DOWNLOADED_ANNOTATED_FILE,
                data: { type: currentMedia?.file.fileType, task, file_uuid: currentMedia?.file.id }
            });
        } catch (error) {
            console.error("Error downloading annotated file:", error);
        } finally {
            setIsDownloading(false);
            setDownloadProgress(0); // Reset progress when done
        }
    }, [
        currentMedia?.file,
        isDownloading,
        setIsDownloading,
        setDownloadProgress,
        predictionsForFile,
        watermarkUrl,
        task,
        track,
        fps
    ]);

    return handleDownload;
}
